import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useWallet } from '@/hooks/useWallet';
import { useGameState } from '@/hooks/useGameState';
import WalletButton from '@/components/web3/WalletButton';
import NetworkBadge from '@/components/web3/NetworkBadge';
import Button from '@/components/ui/Button';
import Card from '@/components/ui/Card';
import LoadingSpinner from '@/components/ui/LoadingSpinner';

const Home: React.FC = () => {
  const navigate = useNavigate();
  const { isConnected } = useWallet();
  const { hasGame, isLoading, error } = useGameState();

  const handleContinue = () => {
    navigate('/game');
  };

  return (
    <div className="min-h-screen w-full flex flex-col items-center justify-center p-4 bg-gradient-to-b from-[#1a1a2e] to-[#0f0e17]">
      {/* Background decoration */}
      <div className="absolute inset-0 opacity-10 bg-[url('/crystal-pattern.svg')] bg-repeat" />

      <div className="relative z-10 max-w-4xl mx-auto w-full">
        {/* Header */}
        <div className="absolute top-4 right-4 flex items-center gap-4">
          <WalletButton />
          <NetworkBadge />
        </div>

        {/* Hero Section */}
        <div className="flex flex-col items-center justify-center min-h-screen gap-8 px-4">
          {/* Title */}
          <div className="text-center">
            <h1 className="text-pixel-xl text-[#e94560] text-shadow mb-2">
              CRYSTALS OF POLKADOT
            </h1>
            <p className="text-pixel-sm text-[#eaeaea] text-shadow">
              An On-Chain JRPG Tribute to Final Fantasy I
            </p>
          </div>

          {/* Subtitle */}
          <p className="text-game text-xl text-center text-shadow max-w-2xl mx-auto">
            Four crystals have lost their light. Four fiends of chaos spread darkness across the land.
            Will you become the Warriors of Light and restore balance to the world?
          </p>

          <Card className="max-w-2xl w-full">
            <div className="space-y-6">
              {/* Wallet Connection Section */}
              <div className="flex flex-col items-center gap-4">
                <WalletButton />
                {isConnected && <NetworkBadge />}
              </div>

              {/* Error display */}
              {error && (
                <Card className="bg-[#d32f2f]/20">
                  <p className="text-pixel-sm text-red-300">{error}</p>
                </Card>
              )}

              {/* Game actions */}
              {isConnected && !isLoading && (
                <div className="space-y-4">
                  {hasGame ? (
                    <>
                      <Button
                        variant="primary"
                        size="lg"
                        className="w-full"
                        onClick={handleContinue}
                      >
                        Continue Adventure
                      </Button>
                      <Button
                        variant="secondary"
                        size="md"
                        className="w-full"
                        onClick={() => navigate('/new-game')}
                      >
                        New Game
                      </Button>
                    </>
                  ) : (
                    <Button
                      variant="primary"
                      size="lg"
                      className="w-full"
                      onClick={() => navigate('/new-game')}
                    >
                      Start Your Journey
                    </Button>
                  )}
                </div>
              )}

              {isLoading && (
                <div className="flex justify-center">
                  <LoadingSpinner text="Loading game state..." />
                </div>
              )}
            </div>
          </Card>

          {/* Features */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-8">
            <Card title="100% On-Chain">
              <p className="text-game text-lg">
                All game logic runs on Polkadot AssetHub smart contracts. Your progress is truly yours.
              </p>
            </Card>
            <Card title="Classic JRPG">
              <p className="text-game text-lg">
                Turn-based combat, character classes, magic spells, and epic boss battles inspired by FF1.
              </p>
            </Card>
            <Card title="Provably Fair">
              <p className="text-game text-lg">
                Commit-reveal randomness ensures fair combat and loot. No hidden server manipulation.
              </p>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Home;
